/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Square shell — Equipo.
 * Scroll unificado: TicketModuleHeader + KPIs + PaperSheet nav blocks.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { ActivityIndicator, RefreshControl, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useEmployeesStore } from '../store/useEmployeesStore';
import { fetchMyInvitations, type InvitationListItem } from '../application/employeeInvitations';
import { useTeamAccess } from '../application/useTeamAccess';
import { listResources } from '../../appointments/infra/appointmentsRepo';
import type { AppointmentResource } from '../../appointments/domain/types';
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { SettingsNavListCard } from '../../settings/screens/components/settingsScreenLayout';
import { SettingsNavigationRow } from '../../settings/components/SettingsNavigationRow';
import { crecePaywallTeamCopy } from '../../../../../shared/plan-crece-copy';
import { trackCreceFunnel } from '../../shell/billing/trackCreceFunnel';
import { CRECE_FUNNEL_EVENTS } from '../../../../../shared/crece-funnel';
import { isBillingEnabled } from '../../shell/billing/isBillingEnabled';
import { CrecePaywallSheet } from '../../shell/billing/components/CrecePaywallSheet';

export function TeamHubScreen() {
  const navigation = useNavigation<any>();
  const insets = useSafeAreaInsets();
  const { layoutWide } = useContentLayout('settings');
  const {
    entLoading: entitlementsLoading,
    canViewTeam,
    canInvite,
    canEditPermissions,
    canHistory,
    allowEmployees,
    isOwner,
  } = useTeamAccess();
  const [teamPaywallVisible, setTeamPaywallVisible] = useState(false);
  const branchId = useSessionStore((state) => state.currentBranchId);
  const { canManageAgenda } = useBranchPermission('Agenda');
  const hasAppointments = useHasBusinessCapability('appointments');
  const employees = useEmployeesStore((state) => state.employees);
  const employeesLoading = useEmployeesStore((state) => state.employeesLoading);
  const loadEmployees = useEmployeesStore((state) => state.loadEmployees);
  const [invitations, setInvitations] = useState<InvitationListItem[]>([]);
  const [resources, setResources] = useState<AppointmentResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const lastLoadStartedAt = useRef(0);
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!canViewTeam) {
      setLoading(false);
      return;
    }
    lastLoadStartedAt.current = Date.now();
    if (!opts?.silent) setLoading(true);
    try {
      await loadEmployees({ force: true });
      const [inviteRows, resourceRows] = await Promise.all([
        canInvite || canEditPermissions ? fetchMyInvitations().catch(() => []) : Promise.resolve([]),
        branchId && hasAppointments ? listResources(branchId).catch(() => []) : Promise.resolve([]),
      ]);
      setInvitations(inviteRows);
      setResources(resourceRows);
    } finally {
      setLoading(false);
    }
  }, [branchId, canEditPermissions, canInvite, canViewTeam, hasAppointments, loadEmployees]);

  useEffect(() => {
    void load();
  }, [load]);

  // Recarga silenciosa al volver: InviteEmployee, EmployeePermissions y
  // Configurar agendas son rutas hijas que mutan personas/invitaciones/agendas
  // — sin esto los KPIs y la lista quedaban stale hasta F5. El dedupe por
  // timestamp evita el doble fetch del mount inicial.
  useFocusEffect(
    useCallback(() => {
      if (Date.now() - lastLoadStartedAt.current < 1500) return;
      void load({ silent: true });
    }, [load])
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  const activeEmployees = useMemo(
    () => employees.filter((employee) => employee.isActive !== false),
    [employees],
  );
  const pendingInvitations = useMemo(
    () => invitations.filter((invitation) => invitation.status === 'pending'),
    [invitations],
  );
  const linkedEmployeeIds = useMemo(() => {
    const linkedEmployeeIds = new Set(
      resources
        .map((resource) => resource.employeeId)
        .filter((employeeId): employeeId is string => Boolean(employeeId)),
    );
    const linkedAuthIds = new Set(
      resources
        .map((resource) => resource.userId)
        .filter((userId): userId is string => Boolean(userId)),
    );
    return new Set(
      activeEmployees
        .filter(
          (employee) =>
            linkedEmployeeIds.has(employee.id) ||
            Boolean(employee.authUserId && linkedAuthIds.has(employee.authUserId)),
        )
        .map((employee) => employee.id),
    );
  }, [activeEmployees, resources]);

  const open = useCallback(
    (route: string, params?: object) => navigateInShell(navigation, route, params),
    [navigation]
  );

  const peopleWithoutAgenda = Math.max(0, activeEmployees.length - linkedEmployeeIds.size);

  // #1463: "Activas" era ambiguo — es la cantidad de personas activas.
  const kpiItems: TicketKpiItem[] = useMemo(() => {
    const items: TicketKpiItem[] = [
      { label: 'Personas activas', value: String(activeEmployees.length) },
    ];
    if (canInvite || canEditPermissions) {
      items.push({
        label: 'Invitaciones',
        value: String(pendingInvitations.length),
        tone: pendingInvitations.length > 0 ? 'warn' : 'default',
        ...(canInvite
          ? {
              onPress: () => open('EmployeePermissions', { initialTab: 'invitations' }),
              testID: 'perfil.team.hub.metric.invitations',
              accessibilityLabel: `Invitaciones pendientes: ${pendingInvitations.length}. Ver invitaciones`,
            }
          : {}),
      });
    }
    if (hasAppointments) {
      items.push({ label: 'Agendas', value: String(resources.filter((r) => r.isActive).length) });
      items.push({ label: 'Con agenda', value: String(linkedEmployeeIds.size) });
    }
    return items;
  }, [activeEmployees.length, pendingInvitations.length, resources, linkedEmployeeIds.size, canInvite, canEditPermissions, hasAppointments, open]);

  if (entitlementsLoading || loading || employeesLoading) {
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <View style={styles.loading}>
          <ActivityIndicator size="large" color={ticketColors.gold} />
          <Text style={styles.loadingText}>Cargando equipo…</Text>
        </View>
      </ShellContentFrame>
    );
  }

  if (!canViewTeam) {
    const showCrecePaywall =
      isBillingEnabled() && isOwner && allowEmployees === false;
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <View style={styles.guardPad}>
          <ModuleEmptyState
            title={showCrecePaywall ? crecePaywallTeamCopy.title : 'Equipo no disponible'}
            subtitle={
              showCrecePaywall
                ? crecePaywallTeamCopy.body
                : 'No tenés permiso para ver el equipo. Pedile al dueño acceso a Equipo.'
            }
            primaryAction={
              showCrecePaywall
                ? {
                    label: crecePaywallTeamCopy.cta,
                    onPress: () => setTeamPaywallVisible(true),
                  }
                : undefined
            }
          />
        </View>
        {showCrecePaywall ? (
          <CrecePaywallSheet
            visible={teamPaywallVisible}
            copy={crecePaywallTeamCopy}
            onActivate={() => {
              setTeamPaywallVisible(false);
              trackCreceFunnel(CRECE_FUNNEL_EVENTS.ctaClick, { source: 'team_paywall' });
              open('ActivateCrece');
            }}
            onDismiss={() => setTeamPaywallVisible(false)}
          />
        ) : null}
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame style={ticketSurfaces.screen}>
      <ModalAwareScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          { paddingBottom: insets.bottom + 24 },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={ticketColors.gold}
            colors={[ticketColors.gold]}
          />
        }
      >
        <View style={styles.stack} testID="perfil.team.hub">
          {/* Header */}
          <TicketModuleHeader
            kicker="Negocio"
            title="Equipo"
            meta={hasAppointments ? 'Personas, permisos y agendas' : 'Personas y permisos'}
            trailing={
              canInvite ? (
                <TouchableOpacity
                  style={styles.inviteBtn}
                  onPress={() => open('InviteEmployee')}
                  testID="perfil.team.invite-btn"
                  accessibilityRole="button"
                  accessibilityLabel="Invitar empleado"
                >
                  <Text style={styles.inviteBtnText}>Invitar empleado</Text>
                </TouchableOpacity>
              ) : null
            }
          />

          {/* KPIs */}
          <PaperSheet>
            <TicketKpiStrip items={kpiItems} variant="soft" layout={layoutWide ? 'desk' : 'mobile'} />
          </PaperSheet>

          {/* Personas */}
          <SettingsNavListCard>
            {canEditPermissions || canViewTeam ? (
              <SettingsNavigationRow
                title="Personas y permisos"
                description="Empleados activos y qué pueden hacer."
                onPress={() => open('EmployeePermissions')}
                iconName="people-outline"
                testID="perfil.team.nav.permissions"
              />
            ) : null}
            {canInvite ? (
              <SettingsNavigationRow
                title="Invitaciones"
                description={
                  pendingInvitations.length > 0
                    ? `${pendingInvitations.length} pendientes de respuesta.`
                    : 'Pendientes, aceptadas o canceladas.'
                }
                onPress={() => open('EmployeePermissions', { initialTab: 'invitations' })}
                iconName="mail-outline"
                testID="perfil.team.nav.invitations"
              />
            ) : null}
            {canHistory ? (
              <SettingsNavigationRow
                title="Historial del equipo"
                description="Cambios de permisos e invitaciones."
                onPress={() => open('TeamHistory')}
                iconName="time-outline"
                showSeparator={false}
                testID="perfil.team.nav.history"
              />
            ) : null}
          </SettingsNavListCard>

          {/* Agendas — solo si el rubro tiene la capability. "Personas sin
              agenda" solo aparece cuando hay alguien pendiente (#1463: la fila
              que solo dice 0 es ruido). */}
          {hasAppointments && (canManageAgenda || peopleWithoutAgenda > 0) ? (
            <SettingsNavListCard>
              {canManageAgenda ? (
                <SettingsNavigationRow
                  title="Configurar agendas"
                  description="Quién atiende, horarios y servicios."
                  onPress={() => open('AppointmentResources')}
                  iconName="calendar-outline"
                  showSeparator={peopleWithoutAgenda > 0}
                />
              ) : null}
              {peopleWithoutAgenda > 0 ? (
                <SettingsNavigationRow
                  title="Personas sin agenda"
                  description={`${peopleWithoutAgenda} ${peopleWithoutAgenda === 1 ? 'persona activa todavía no tiene' : 'personas activas todavía no tienen'} agenda.`}
                  onPress={() => open('EmployeePermissions')}
                  iconName="person-add-outline"
                  showSeparator={false}
                />
              ) : null}
            </SettingsNavListCard>
          ) : null}
        </View>
      </ModalAwareScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
  },
  stack: {
    gap: 10,
    alignSelf: 'stretch',
  },
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    padding: spacing.lg,
  },
  loadingText: {
    fontSize: 15,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  guardPad: {
    flex: 1,
    padding: spacing.lg,
    justifyContent: 'center',
  },
  inviteBtn: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
    minHeight: 42,
    justifyContent: 'center',
  },
  inviteBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
});
